/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useState } from 'react'

import { EuiFlexGroup, EuiFlexItem, EuiSkeletonText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type {
  NormalisedResourceRoleAssignment,
  ResourceType,
  Resource,
} from '@modules/role-assignments-lib/types'
import {
  filterItemsByIdOrName,
  specificRoleAssignmentsToTableItems,
} from '@modules/role-assignments-lib'
import RoleAssignmentsFilter from '@modules/role-assignments-components/RoleAssignmentsFilter'
import { CuiAlert } from '@modules/cui/Alert'
import type { ApiErrorCollection } from '@modules/query/types'

import ProjectRoleAssignmentsTable from './ProjectRoleAssignmentsTable'

import type { FunctionComponent } from 'react'

export type MemberOrApiKey = 'member' | 'api-key'

export type UserDeploymentsAndProjectsQueryResult<T extends ResourceType = ResourceType> = {
  resourceType: T
  data: Array<Resource<T>> | undefined
  isLoading: boolean
  error: unknown
}

export interface ProjectRoleAssignmentsSummaryProps {
  memberOrApiKey: MemberOrApiKey
  organizationDeploymentsAndProjectsQueryResults: UserDeploymentsAndProjectsQueryResult[]
  roleAssignments: Array<NormalisedResourceRoleAssignment<ResourceType>>
  customRolesEnabled: boolean
  observabilityCustomRoles: boolean
}

const ProjectRoleAssignmentsSummary: FunctionComponent<ProjectRoleAssignmentsSummaryProps> = ({
  memberOrApiKey,
  organizationDeploymentsAndProjectsQueryResults: queryResults,
  roleAssignments,
  customRolesEnabled,
  observabilityCustomRoles,
}) => {
  const [filterQuery, setFilterQuery] = useState('')

  if (queryResults.some(({ isLoading }) => isLoading)) {
    return <EuiSkeletonText />
  }

  const items = queryResults.flatMap((queryResult) =>
    specificRoleAssignmentsToTableItems(
      queryResult.resourceType,
      queryResult.data,
      roleAssignments,
      memberOrApiKey === 'api-key',
    ).filter(({ roles }) => roles.length !== 0),
  )
  const filteredItems = filterItemsByIdOrName(items, filterQuery)

  return (
    <EuiFlexGroup direction='column' gutterSize='l'>
      {renderError()}

      <EuiFlexItem>
        {memberOrApiKey === 'member' ? (
          <FormattedMessage
            id='roles-assignments.role-assignments-summary.member'
            defaultMessage='This member has specific access to the following cloud resources:'
          />
        ) : (
          <FormattedMessage
            id='roles-assignments.role-assignments-summary.api-key'
            defaultMessage='This API key provides access to the following cloud resources:'
          />
        )}
      </EuiFlexItem>

      <EuiFlexItem>
        <RoleAssignmentsFilter onChange={setFilterQuery} />
      </EuiFlexItem>

      <EuiFlexItem>
        <ProjectRoleAssignmentsTable
          items={filteredItems}
          customRolesEnabled={customRolesEnabled}
          observabilityCustomRoles={observabilityCustomRoles}
          isApiKey={memberOrApiKey === 'api-key'}
        />
      </EuiFlexItem>
    </EuiFlexGroup>
  )

  function renderError() {
    const errorResult = queryResults.find(({ error }) => error)?.error as
      | ApiErrorCollection
      | undefined

    if (!errorResult) {
      return null
    }

    if (
      typeof errorResult === 'object' &&
      'errors' in errorResult &&
      errorResult.errors.length > 0
    ) {
      return errorResult.errors.map((err, index) => (
        <EuiFlexItem key={index}>
          <CuiAlert type='danger'>{err.message}</CuiAlert>
        </EuiFlexItem>
      ))
    }

    return (
      <EuiFlexItem>
        <CuiAlert type='danger'>
          <FormattedMessage
            id='roles-assignments.role-assignments-summary.error'
            defaultMessage='Something went wrong'
          />
        </CuiAlert>
      </EuiFlexItem>
    )
  }
}

export default ProjectRoleAssignmentsSummary
